import * as Rx from 'rxjs';
import * as RxOp from 'rxjs/operators';
import { getPosApiUrl, getAccessToken } from '../../utils/state';
import {
  getOrder,
  holdOrder,
  cancelOrderById,
  removeOrderItem,
  voidTransactionById,
  fetchOrderDetails,
  pollByPaymentIdentifier,
} from '../../utils/api/client';
import {
  cancelOrderSuccess,
  cartAlertBarAction,
  cartModalAction,
  GET_ORDER_REQUEST,
  getOrderRequest,
  getOrderError,
  getOrderSuccess,
  HOLD_ORDER_REQUEST,
  holdOrderSuccess,
  REMOVE_ORDER_ITEM_REQUEST,
  removeOrderItemSuccess,
  removeOrderItemError,
  SUBMIT_CANCEL_ORDER_REQUEST,
  INITIATE_VOID_TRANSACTION,
  voidTransactionError,
  voidTransactionSuccess,
  stopVoidTransactionPolling,
  STOP_VOID_TRANSACTION_POLLING,
  GET_UPDATED_CART_REQUEST,
  getUpdatedCartSuccess,
  cancelOrderError,
} from './Cart.state';
import { resetPinPadRequest } from '../../state/shared';
import { caughtError } from '../../utils/api/errorHandler';
import { closeModal } from '../../components/modal/Modal.state';
import { getOrderDetailsParams } from '../order-details/OrderDetails.epic';
import { genericErrorMessage, replaceSharedAlerts } from '../../state/shared';
import { TRANSACTION_STATUS } from '../../utils/constants';
import { createDpsVoidPolling } from '../../utils/dpsVoidPolling';
import { EMPTY_ACTION } from '../../state';
import { cartCancellationRequiresAssistance } from './orderCancellation';

const caughtCartErrorCallback = (state) =>
  caughtError(cartAlertBarAction, 'Cart Page Error:', state);

const getOrderParams = (state) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
});
export const getCurrentOrderEpic = (action$, state$) =>
  action$.ofType(GET_ORDER_REQUEST).pipe(
    RxOp.exhaustMap(() =>
      Rx.from(getOrder(getOrderParams(state$.value))).pipe(
        RxOp.flatMap((orderResponse) =>
          Rx.of(getOrderSuccess(orderResponse.data)),
        ),
        RxOp.catchError((err) =>
          caughtCartErrorCallback(state$.value)(
            err,
            getOrderError(err.message),
          ),
        ),
      ),
    ),
  );

export const getUpdatedCartEpic = (action$, state$) =>
  action$.ofType(GET_UPDATED_CART_REQUEST).pipe(
    RxOp.flatMap((action) =>
      Rx.from(
        fetchOrderDetails(getOrderDetailsParams(state$.value, action.payload)),
      ).pipe(
        RxOp.flatMap((response) =>
          Rx.of(
            getUpdatedCartSuccess({
              id: response.data,
              navigate: action.payload.navigate,
              location: action.payload.location,
              data: response.data,
            }),
          ),
        ),
        RxOp.catchError((err) => caughtCartErrorCallback(state$.value)(err)),
      ),
    ),
  );

const holdOrderParams = getOrderParams;
export const holdOrderEpic = (action$, state$) =>
  action$.ofType(HOLD_ORDER_REQUEST).pipe(
    RxOp.flatMap((action) =>
      Rx.from(holdOrder(holdOrderParams(state$.value))).pipe(
        RxOp.flatMap((orderResponse) => {
          action.payload.navigate('/pos/products');
          return Rx.of(holdOrderSuccess(orderResponse.data));
        }),
        RxOp.catchError((err) => caughtCartErrorCallback(state$.value)(err)),
      ),
    ),
  );

const cancelOrderByIdParams = (state, action) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  note: `${state.cart.forms.cancelOrderForm.reason} ${state.cart.forms.cancelOrderForm.note}`,
  id: action.payload.id,
});

const cancellationAssistanceAlert = (orderId) =>
  replaceSharedAlerts({
    heading: 'Order Cancellation Assistance Required',
    text: `Order ${orderId} could not be cancelled automatically and requires assistance. You may begin a new order and view this order in the order history page.`,
    variant: 'error',
  });

export const cancelOrderByIdEpic = (action$, state$) =>
  action$.ofType(SUBMIT_CANCEL_ORDER_REQUEST).pipe(
    RxOp.flatMap((action) =>
      Rx.from(
        cancelOrderById(cancelOrderByIdParams(state$.value, action)),
      ).pipe(
        RxOp.flatMap((cancelOrderResponse) => {
          action.payload.navigate('/pos/products');
          const actions = [
            cancelOrderSuccess(cancelOrderResponse),
            cartModalAction(closeModal()),
          ];
          if (cartCancellationRequiresAssistance(cancelOrderResponse)) {
            actions.push(cancellationAssistanceAlert(action.payload.id));
          }
          return Rx.from(actions);
        }),
        RxOp.catchError((err) =>
          Rx.of(
            cancelOrderError(err.message),
            replaceSharedAlerts(
              genericErrorMessage(err, 'Order Cancellation Error'),
            ),
          ),
        ),
      ),
    ),
  );

const removeItemParams = (state, payload) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  item_id: payload,
});
export const removeItemEpic = (action$, state$) =>
  action$.ofType(REMOVE_ORDER_ITEM_REQUEST).pipe(
    RxOp.flatMap((action) =>
      Rx.from(
        removeOrderItem(removeItemParams(state$.value, action.payload)),
      ).pipe(
        RxOp.flatMap((orderResponse) =>
          Rx.of(removeOrderItemSuccess(orderResponse.data)),
        ),
        RxOp.catchError((err) =>
          caughtCartErrorCallback(state$.value)(err, removeOrderItemError()),
        ),
      ),
    ),
  );

const voidTransactionByIdParams = (state, action) => ({
  url: getPosApiUrl(state),
  token: getAccessToken(state),
  id: action.payload.id,
  method: 'PUT',
  payment_attempt_unique_identifier:
    action.payload?.payment_attempt_unique_identifier,
});

const VOID_POLL_OUTCOME = {
  CANCELLED: 'cancelled',
  RESTORED: 'restored',
};

const classifyVoidPollOutcome = (pollResponse) => {
  switch (pollResponse.data?.status) {
    case TRANSACTION_STATUS.CANCELLED:
      return VOID_POLL_OUTCOME.CANCELLED;
    case TRANSACTION_STATUS.SUCCESSFUL:
      return VOID_POLL_OUTCOME.RESTORED;
    default:
      return null;
  }
};

export const voidTransactionByIdEpic = (action$, state$) =>
  action$.ofType(INITIATE_VOID_TRANSACTION).pipe(
    RxOp.flatMap((action) =>
      Rx.from(
        voidTransactionById(voidTransactionByIdParams(state$.value, action)),
      ).pipe(
        RxOp.flatMap((response) => {
          // For DPS (Datacap) card voids, the backend sets the payment to
          // cancel_pending. Poll until it is cancelled or restored.
          if (
            action.payload.isDatacapTransaction &&
            action.payload.payment_attempt_unique_identifier
          ) {
            const terminalId =
              state$.value.terminalLogin.terminalSession.terminal_id;

            return createDpsVoidPolling({
              pollFn: () =>
                Rx.from(
                  pollByPaymentIdentifier({
                    url: getPosApiUrl(state$.value),
                    token: getAccessToken(state$.value),
                    paymentAttemptUniqueId:
                      action.payload.payment_attempt_unique_identifier,
                  }),
                ),
              isDone: (pollResponse) =>
                Boolean(classifyVoidPollOutcome(pollResponse)),
              onSuccess: (pollResponse) => {
                switch (classifyVoidPollOutcome(pollResponse)) {
                  case VOID_POLL_OUTCOME.RESTORED:
                    return Rx.of(
                      voidTransactionError('Void unsuccessful'),
                      resetPinPadRequest(terminalId),
                      replaceSharedAlerts({
                        heading: 'Void Unsuccessful',
                        text: 'The payment was restored and remains in the cart.',
                        variant: 'error',
                      }),
                      getOrderRequest(),
                      stopVoidTransactionPolling(),
                    );
                  case VOID_POLL_OUTCOME.CANCELLED:
                    return Rx.of(
                      voidTransactionSuccess({
                        ...response,
                        id: action.payload.id,
                      }),
                      resetPinPadRequest(terminalId),
                      replaceSharedAlerts({
                        heading: 'Transaction Voided',
                        text: 'The card payment has been successfully voided.',
                        variant: 'success',
                      }),
                      stopVoidTransactionPolling(),
                    );
                  default:
                    return Rx.EMPTY;
                }
              },
              onTimeout: () =>
                Rx.of(
                  voidTransactionError('Void timed out'),
                  resetPinPadRequest(terminalId),
                  replaceSharedAlerts({
                    heading: 'Void Status',
                    text: 'Void is still processing. Please refresh the page to check the latest status.',
                    variant: 'warn',
                  }),
                  getOrderRequest(),
                  stopVoidTransactionPolling(),
                ),
              onError: (pollErr) => {
                console.error(
                  '[voidTransactionByIdEpic] Poll failed:',
                  pollErr?.message ?? 'unknown error',
                );
                return Rx.of(
                  voidTransactionError(pollErr?.message ?? 'unknown error'),
                  resetPinPadRequest(terminalId),
                  replaceSharedAlerts({
                    heading: 'Void Error',
                    text: 'Void failed. Please check the transaction status.',
                    variant: 'error',
                  }),
                  getOrderRequest(),
                  stopVoidTransactionPolling(),
                );
              },
              stopSignal$: action$.ofType(STOP_VOID_TRANSACTION_POLLING),
            });
          }

          // Non-DPS voids complete synchronously
          return Rx.of(
            voidTransactionSuccess({ ...response, id: action.payload.id }),
            replaceSharedAlerts({
              heading: 'Transaction Voided',
              text: 'The payment has been successfully voided.',
              variant: 'success',
            }),
          );
        }),
        RxOp.catchError((err) =>
          Rx.of(
            voidTransactionError(err?.message ?? 'unknown error'),
            action.payload.isDatacapTransaction
              ? resetPinPadRequest(
                  state$.value.terminalLogin.terminalSession.terminal_id,
                )
              : EMPTY_ACTION,
            replaceSharedAlerts(genericErrorMessage(err)),
          ),
        ),
      ),
    ),
  );
